import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Jurisdictions — admin list of per-state Large Projects legal
// rules: whether the state is enabled, legally validated, down-payment caps,
// cancellation windows and disclosure requirements.
//
// Consumes besthand-api `listJurisdictions` (returns `jurisdictionRules`)
// through the `/lp-graphql` proxy via `lpApi`. Admin-gated (requireAdmin).
// ============================================================================

const Q_JURISDICTIONS = `
  query {
    listJurisdictions {
      status errorMessage
      jurisdictionRules {
        id state countryCode legallyValidated largeProjectsEnabled
        downPaymentCapAmount downPaymentCapPercent
        cancellationDaysStandard cancellationDaysSenior seniorAgeThreshold
        requiresLienWaiver requiresSubcontractorDisclosure requiresMechanicsLienNotice
        contractTemplateId validatedAt validatedBy
      }
    }
  }
`;

// validateJurisdiction(state!, contractTemplateId, notes) — flips
// legallyValidated=true AND largeProjectsEnabled=true.
const M_VALIDATE = `
  mutation ($state: String!, $contractTemplateId: String, $notes: String) {
    validateJurisdiction(state: $state, contractTemplateId: $contractTemplateId, notes: $notes) {
      status errorMessage
      jurisdictionRule { id state legallyValidated largeProjectsEnabled }
    }
  }
`;

// invalidateJurisdiction(state!, reason!) — flips both flags to false.
const M_INVALIDATE = `
  mutation ($state: String!, $reason: String!) {
    invalidateJurisdiction(state: $state, reason: $reason) {
      status errorMessage
      jurisdictionRule { id state legallyValidated largeProjectsEnabled }
    }
  }
`;

const S = {
  page: { maxWidth: 1180, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: 0, overflow: 'hidden', boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, padding: '14px 16px', borderBottom: '1px solid #eef1f3', background: '#fafbfc', whiteSpace: 'nowrap' },
  td: { padding: '14px 16px', borderBottom: '1px solid #f3f5f6', fontSize: 14, verticalAlign: 'middle' },
  state: { fontSize: 15, fontWeight: 800, color: '#0f1214' },
  pill: (on, kind) => {
    let bg = '#eef2f4'; let fg = '#5a6168';
    if (kind === 'enabled') { bg = on ? '#e7f6ec' : '#fde7e8'; fg = on ? '#137a37' : '#c2151c'; }
    if (kind === 'validated') { bg = on ? '#e7f6ec' : '#fff3d6'; fg = on ? '#137a37' : '#946200'; }
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', padding: '3px 9px', borderRadius: 999, background: bg, color: fg, whiteSpace: 'nowrap' };
  },
  req: (on) => ({ fontSize: 12, fontWeight: 700, color: on ? '#137a37' : '#b9bfc5' }),
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  btnSm: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
  btnRedSm: { background: '#fff', color: '#c2151c', border: '1px solid #f3c9cb', borderRadius: 8, padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' },
};

const fmtCap = (r) => {
  const parts = [];
  if (r.downPaymentCapAmount != null) parts.push(`$${Number(r.downPaymentCapAmount).toLocaleString()}`);
  if (r.downPaymentCapPercent != null) parts.push(`${Number(r.downPaymentCapPercent)}%`);
  return parts.length ? parts.join(' / ') : '—';
};
const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleDateString(); } catch (e) { return s; } };

function Jurisdictions() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_JURISDICTIONS);
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.listJurisdictions;
        if (env && env.status === 200) setRules(env.jurisdictionRules || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load jurisdictions: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, query, variables, opName, okText) => {
    setBusyId(key);
    setMsg(null);
    try {
      const res = await lpApi(query, variables);
      if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
      const env = res.data && res.data[opName];
      if (env && env.status >= 200 && env.status < 300) {
        setMsg({ ok: okText });
        await load();
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Action failed: ${e.message}` });
    }
    setBusyId('');
  };

  const validate = (r) => {
    const tpl = typeof window !== 'undefined' ? window.prompt(`Validate ${r.state} for Large Projects.\nThis flips Validated AND Enabled to ON.\n\nContract template ID (leave blank to keep ${r.contractTemplateId || 'none'}):`) : '';
    if (tpl === null) return; // cancelled
    const notes = typeof window !== 'undefined' ? window.prompt(`Optional validation notes for ${r.state} (leave blank to skip):`) : '';
    if (notes === null) return;
    run(
      `j${r.state}`,
      M_VALIDATE,
      { state: r.state, contractTemplateId: tpl.trim() ? tpl.trim() : null, notes: notes.trim() ? notes.trim() : null },
      'validateJurisdiction',
      `${r.state} validated and enabled.`,
    );
  };

  const invalidate = (r) => {
    const reason = typeof window !== 'undefined' ? window.prompt(`Invalidate ${r.state}? This disables Large Projects there immediately.\nReason (required, ≥ 3 chars):`) : '';
    if (!reason || reason.trim().length < 3) { if (reason !== null) setMsg({ error: 'A reason of at least 3 characters is required.' }); return; }
    run(`j${r.state}`, M_INVALIDATE, { state: r.state, reason: reason.trim() }, 'invalidateJurisdiction', `${r.state} invalidated and disabled.`);
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Jurisdictions</h2>
      <div style={S.hint}>
        Large Projects — per-state legal rules. Shows whether the state is open for projects, whether the rules
        are legally validated, the down-payment cap and cancellation windows. Validate flips a state ON (validated
        + enabled); invalidate disables it immediately.
      </div>

      <div style={S.toolbar}>
        <button style={S.btnGhost} onClick={() => load()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{rules.length} states</span>
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        rules.length === 0 ? (
          <div style={S.empty}>No jurisdiction rules configured.</div>
        ) : (
          <div style={S.card}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>State</th>
                  <th style={S.th}>Enabled</th>
                  <th style={S.th}>Validated</th>
                  <th style={S.th}>Down-payment cap</th>
                  <th style={S.th}>Cancellation (std / senior)</th>
                  <th style={S.th}>Requires</th>
                  <th style={S.th}>Validated at</th>
                  <th style={S.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => (
                  <tr key={r.id}>
                    <td style={S.td}>
                      <span style={S.state}>{r.state}</span>
                      {r.countryCode ? <span style={{ color: '#9ca3af', fontSize: 12, marginLeft: 6 }}>{r.countryCode}</span> : null}
                    </td>
                    <td style={S.td}><span style={S.pill(r.largeProjectsEnabled, 'enabled')}>{r.largeProjectsEnabled ? 'ENABLED' : 'OFF'}</span></td>
                    <td style={S.td}><span style={S.pill(r.legallyValidated, 'validated')}>{r.legallyValidated ? 'VALIDATED' : 'PENDING'}</span></td>
                    <td style={S.td}>{fmtCap(r)}</td>
                    <td style={S.td}>
                      {r.cancellationDaysStandard != null ? `${r.cancellationDaysStandard}d` : '—'}
                      {' / '}
                      {r.cancellationDaysSenior != null ? `${r.cancellationDaysSenior}d` : '—'}
                      {r.seniorAgeThreshold ? ` (${r.seniorAgeThreshold}+)` : ''}
                    </td>
                    <td style={S.td}>
                      <span style={{ ...S.req(r.requiresLienWaiver), marginRight: 8 }}>Lien waiver</span>
                      <span style={{ ...S.req(r.requiresSubcontractorDisclosure), marginRight: 8 }}>Sub. disclosure</span>
                      <span style={S.req(r.requiresMechanicsLienNotice)}>Mech. lien notice</span>
                    </td>
                    <td style={S.td}>
                      {r.validatedAt ? fmtDate(r.validatedAt) : '—'}
                      {r.validatedBy ? <div style={{ color: '#9ca3af', fontSize: 12 }}>by {r.validatedBy}</div> : null}
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {r.legallyValidated ? (
                          <button style={S.btnRedSm} disabled={busyId === `j${r.state}`} onClick={() => invalidate(r)}>
                            {busyId === `j${r.state}` ? '…' : 'Invalidate'}
                          </button>
                        ) : (
                          <button style={S.btnSm} disabled={busyId === `j${r.state}`} onClick={() => validate(r)}>
                            {busyId === `j${r.state}` ? '…' : 'Validate'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

export default Jurisdictions;
